import React, { useCallback, useId, useMemo, useRef, useState } from "react"

import { Textarea } from "@coval/ui"
import { Tooltip } from "@coval/ui/tooltip"
import { ClipboardPaste, Info, Trash2 } from "lucide-react"

import type { MetricMetadata } from "@/types/metrics/metadata"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"

type HumanReviewInstructions = NonNullable<
  MetricMetadata["human_review_instructions"]
>

/**
 * Parses TSV with quoted-field support (Google Docs / Excel style).
 * Quoted fields may contain newlines and escaped quotes ("").
 */
function parseTsv(text: string): string[][] {
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentField = ""
  let inQuotes = false
  let i = 0

  while (i < text.length) {
    const char = text[i]

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          currentField += '"'
          i += 2
          continue
        }
        inQuotes = false
        i++
        continue
      }
      currentField += char
      i++
      continue
    }

    if (char === '"') {
      inQuotes = true
      i++
      continue
    }

    if (char === "\t") {
      currentRow.push(currentField)
      currentField = ""
      i++
      continue
    }

    if (char === "\n" || (char === "\r" && text[i + 1] === "\n")) {
      currentRow.push(currentField)
      currentField = ""
      if (currentRow.some((cell) => cell.length > 0)) {
        rows.push(currentRow)
      }
      currentRow = []
      i += char === "\r" ? 2 : 1
      continue
    }

    if (char === "\r") {
      currentRow.push(currentField)
      currentField = ""
      if (currentRow.some((cell) => cell.length > 0)) {
        rows.push(currentRow)
      }
      currentRow = []
      i++
      continue
    }

    currentField += char
    i++
  }

  currentRow.push(currentField)
  if (currentRow.some((cell) => cell.length > 0)) {
    rows.push(currentRow)
  }

  return rows
}

interface HumanReviewInstructionsEditorProps {
  value: HumanReviewInstructions | undefined
  onChange: (value: HumanReviewInstructions) => void
}

export const HumanReviewInstructionsEditor: React.FC<
  HumanReviewInstructionsEditorProps
> = ({ value, onChange }) => {
  const [showPasteArea, setShowPasteArea] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const instructionsTextId = useId()

  const text = value?.text ?? ""
  const rows = useMemo(() => value?.table ?? [], [value?.table])
  const hasTableData = rows.length > 0

  const updateField = useCallback(
    (updates: Partial<HumanReviewInstructions>) => {
      onChange({ ...value, ...updates })
    },
    [value, onChange],
  )

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateField({ text: e.target.value })
    },
    [updateField],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      e.preventDefault()
      const pastedText = e.clipboardData.getData("text/plain")
      if (!pastedText.trim()) {
        return
      }
      const parsed = parseTsv(pastedText)
      if (parsed.length > 0) {
        updateField({ table: parsed })
        setShowPasteArea(false)
      }
    },
    [updateField],
  )

  const handleClearTable = useCallback(() => {
    updateField({ table: [] })
  }, [updateField])

  const handleCellChange = useCallback(
    (rowIndex: number, colIndex: number, newValue: string) => {
      const updated = rows.map((row, ri) =>
        ri === rowIndex
          ? row.map((cell, ci) => (ci === colIndex ? newValue : cell))
          : row,
      )
      updateField({ table: updated })
    },
    [rows, updateField],
  )

  const handleShowPaste = useCallback(() => {
    setShowPasteArea(true)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }, [])

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <label
          htmlFor={instructionsTextId}
          id={`${instructionsTextId}-label`}
          className={LABEL_STYLES}
        >
          Human Review Instructions
        </label>
        <Tooltip content="Add written instructions and/or paste a reference table from Google Docs or a spreadsheet.">
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>

      <div className="space-y-4">
        <Textarea
          id={instructionsTextId}
          aria-labelledby={`${instructionsTextId}-label`}
          className="min-h-[300px] max-h-[600px]"
          value={text}
          onChange={handleTextChange}
          placeholder="Type general instructions for human reviewers here..."
        />

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-coval-text-muted">
              Reference Table
            </span>
            <button
              type="button"
              onClick={handleShowPaste}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-coval-text-muted bg-surface-1 hover:bg-surface-2 rounded-lg transition-colors"
              aria-label="Paste table data"
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              {hasTableData ? "Re-paste Table" : "Paste Table"}
            </button>
            {hasTableData && (
              <button
                type="button"
                onClick={handleClearTable}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-coval-danger-on-surface bg-coval-danger-surface hover:bg-coval-danger-surface-hover rounded-lg transition-colors"
                aria-label="Clear table data"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>

          {showPasteArea && (
            <div className="bg-surface-1 rounded-lg p-4">
              <Textarea
                ref={textareaRef}
                className="min-h-[100px] border-dashed"
                placeholder="Paste your table here (from Google Docs, Excel, or any spreadsheet)..."
                onPaste={handlePaste}
                aria-label="Paste area for table data"
              />
              <p className="text-xs text-coval-text-muted mt-2">
                Paste tab-separated data. The first row will be used as column
                headers. Multi-line cells (quoted fields) are supported.
              </p>
            </div>
          )}

          {hasTableData && (
            <div className="overflow-x-auto rounded-lg border border-coval-border">
              <table className="min-w-full text-sm table-fixed">
                <thead>
                  <tr className="bg-surface-1">
                    {rows[0]?.map((header, ci) => (
                      <th
                        key={ci}
                        className="px-3 py-2 text-left text-xs font-semibold text-coval-text-muted border-b border-coval-border min-w-[180px]"
                      >
                        <input
                          type="text"
                          value={header}
                          onChange={(e) =>
                            handleCellChange(0, ci, e.target.value)
                          }
                          className="w-full bg-transparent border-none focus:outline-none rounded px-1 text-xs font-semibold"
                          aria-label={`Header column ${ci + 1}`}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(1).map((row, ri) => (
                    <tr
                      key={ri}
                      className="border-b border-coval-border last:border-b-0 hover:bg-surface-1 align-top"
                    >
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="px-3 py-2 text-sm text-coval-text whitespace-pre-wrap break-words"
                        >
                          <textarea
                            value={cell}
                            onChange={(e) =>
                              handleCellChange(ri + 1, ci, e.target.value)
                            }
                            rows={Math.max(2, cell.split("\n").length + 1)}
                            className="w-full bg-transparent border-none focus:outline-none rounded px-1 text-sm text-coval-text resize-vertical"
                            aria-label={`Row ${ri + 1}, Column ${ci + 1}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
